<template>
	<div class="actions">
		<ResourceRecommendComponent :resource="resource" />

		<ResourceFavouriteComponent :resource="resource" />

		<template v-if="pdfUrl">
			<a
				:href="pdfUrl"
				download
				target="_blank"
				title="Download PDF"
				@click="trackButtonEvent('downloadCustomPdf')"
			>
				<DownloadIconComponent />
			</a>

			<a
				:href="pdfUrl"
				download
				target="_blank"
				title="Print PDF"
				@click="trackButtonEvent('printCustomPdf')"
			>
				<PrintIconComponent />
			</a>
		</template>
		<template v-else>
			<button
				class="pdf-download"
				title="Download PDF"
				@click="
					{
						trackButtonEvent('printPage');
						print();
					}
				"
			>
				<PrintIconComponent />
			</button>
		</template>

		<PopupComponent ref="safariConfirmation">
			<template #content="scope">
				<p>For best results use the Chrome browser to print this resource.</p>
				<p>
					<a
						href="https://www.google.com/intl/en_au/chrome/"
						target="_blank"
						>You can download Chrome here</a
					>.
				</p>
				<p>
					<label>
						<input
							v-model="dontShowSafariAgain"
							type="checkbox"
						/>
						Don't show this message again
					</label>
				</p>
				<button
					class="btn btn-primary btn-small pdf-download"
					title="Print PDF"
					@click="
						{
							scope.close();
							trackButtonEvent('hideSafariConfirmationAndPrint');
							handleSafariDontShowAgainAndPrint();
						}
					"
				>
					Print using Safari
				</button>
			</template>
		</PopupComponent>
	</div>
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue';
import { mapActions, mapState } from 'pinia';
import { event } from 'vue-gtag';
import DownloadIconComponent from '@/images/download.svg?component';
import PrintIconComponent from '@/images/print.svg?component';
import type { Resource } from '@/interfaces/resourceInterface';
import { useUser } from '@/stores/userStore';
import ResourceFavouriteComponent from '@/components/resources/ResourceFavouriteComponent.vue';
import ResourceRecommendComponent from '@/components/resources/ResourceRecommendComponent.vue';
import PopupComponent from '@/components/shared/PopupComponent.vue';

export default defineComponent({
	components: { ResourceFavouriteComponent, DownloadIconComponent, PrintIconComponent, PopupComponent, ResourceRecommendComponent },
	props: {
		resource: {
			type: Object as PropType<Resource>,
			required: true,
		},
		disablePrint: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			dontShowAgain: false,
			dontShowSafariAgain: false,
		};
	},
	computed: {
		pdfUrl(): string | false {
			return this.resource.pdf || this.resource.pdf_generated || false;
		},
		...mapState(useUser, ['toggles']),
	},
	methods: {
		handleSafariDontShowAgainAndPrint() {
			if (this.dontShowSafariAgain) {
				this.updateToggles('hideSafariResourcePrintAdvice', true);
			}

			window.print();
		},
		print() {
			// https://stackoverflow.com/a/23522755/2518608
			const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
			if (isSafari && !this.toggles.hideSafariResourcePrintAdvice && this.$refs.safariConfirmation) {
				(this.$refs.safariConfirmation as InstanceType<typeof PopupComponent>).open();
			} else {
				window.print();
			}
		},
		trackButtonEvent(action: string) {
			event('button-click', {
				component: 'ResourceActionsComponent',
				context: 'downloadingPrintingResource',
				action,
			});
		},
		...mapActions(useUser, ['updateToggles']),
	},
});
</script>

<style scoped lang="scss">
.actions {
	display: flex;
	justify-content: center;
	gap: 20px;

	:deep() {
		svg {
			width: 25px;
		}
	}
}
</style>
